import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ENQUIRY_STATUSES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, ExternalLink } from "lucide-react";

const STATUS_COLOR = {
  New: "bg-ink text-white",
  Contacted: "bg-sand text-ink",
  "Consultation Scheduled": "bg-sand text-ink",
  "Quotation Sent": "bg-sand text-ink",
  Converted: "bg-accent text-white",
  Closed: "bg-line text-stone",
};

export default function PortalEnquiries() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await api.get("/enquiries", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const updateEnquiry = async (patch) => {
    try {
      const { data } = await api.patch(`/enquiries/${open.id}`, patch);
      setOpen(data); load();
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const cols = ["created_at", "status", "full_name", "email", "phone", "project_type", "location", "budget", "preferred_service", "description"];
    const csv = [cols.join(",")].concat(items.map((r) => cols.map((c) => JSON.stringify(r[c] || "")).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "enquiries.csv";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="p-6 lg:p-12" data-testid="portal-enquiries">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-1">Studio</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">Enquiries</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-9 w-56 bg-white" data-testid="enquiries-search" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 bg-white border border-line px-3 pr-8 text-sm text-ink focus:outline-none focus:border-ink min-w-[180px]" data-testid="enquiries-status">
            <option value="">All statuses</option>
            {ENQUIRY_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <Button variant="outline" onClick={exportCsv} className="rounded-none border-line" data-testid="enquiries-export">
            <ExternalLink size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-stone">
            <tr className="border-b border-line">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Service</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-b border-line hover:bg-sand/40 cursor-pointer" onClick={() => setOpen(e)} data-testid={`enq-row-${e.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-stone">{e.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{e.full_name}</div>
                  <div className="text-xs text-stone">{e.email}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-stone">{e.preferred_service || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell text-stone">{e.location || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${STATUS_COLOR[e.status] || "bg-sand text-ink"}`}>{e.status}</span>
                </td>
                <td className="px-4 py-3 text-right text-xs uppercase tracking-widest">Open →</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="p-14 text-center text-stone" data-testid="enquiries-empty">No enquiries.</div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-light text-2xl">{open.full_name}</DialogTitle>
                <div className="text-sm text-stone">
                  <a href={`mailto:${open.email}`} className="hover:text-ink">{open.email}</a>
                  {open.phone && <> · <a href={`tel:${open.phone}`} className="hover:text-ink">{open.phone}</a></>}
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                <Info label="Project type" value={open.project_type} />
                <Info label="Location" value={open.location} />
                <Info label="Budget" value={open.budget} />
                <Info label="Service" value={open.preferred_service} />
              </div>

              <div>
                <div className="eyebrow mb-2">Description</div>
                <div className="text-sm whitespace-pre-wrap border border-line p-3 bg-sand/50 max-h-40 overflow-y-auto">{open.description}</div>
              </div>

              <div>
                <div className="eyebrow mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {ENQUIRY_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateEnquiry({ status: s })}
                      className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${open.status === s ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                      data-testid={`enq-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="eyebrow mb-2">Internal notes</div>
                <Textarea
                  rows={4}
                  value={open.internal_notes || ""}
                  onChange={(e) => setOpen({ ...open, internal_notes: e.target.value })}
                  onBlur={() => updateEnquiry({ internal_notes: open.internal_notes })}
                  data-testid="enq-notes"
                />
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-line p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone">{label}</div>
      <div className="mt-1">{value || "—"}</div>
    </div>
  );
}
